import { useState } from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import { FormDialog } from "../../../../components/primitives/feedback/FormDialog/FormDialog";
import { useFormDialog } from "../../../../components/primitives/feedback/FormDialog/useFormDialog";
import { ADMISSION_CODE_VALUES, type AdmissionCode } from "@shared/types/rejections.ts";

export interface RejectDocumentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the chosen code and message when the user confirms. Throw to surface an error
   *  and keep the dialog open. */
  onConfirm: (code: AdmissionCode, message: string) => Promise<void>;
}

/** Collects a rejection code and optional message before calling onConfirm. */
export function RejectDocumentDialog({ open, onClose, onConfirm }: RejectDocumentDialogProps) {
  const [code, setCode] = useState<AdmissionCode | "">("");
  const [message, setMessage] = useState("");

  const { dialogProps } = useFormDialog({
    onSubmit: async () => {
      if (!code) throw new Error("Select a reason before rejecting.");
      await onConfirm(code, message || code);
    },
    onClose: () => {
      setCode("");
      setMessage("");
      onClose();
    },
  });

  return (
    <FormDialog
      open={open}
      title="Reject document"
      submitLabel="Reject"
      maxWidth="xs"
      submitDisabled={!code}
      {...dialogProps}
    >
      <FormControl fullWidth size="small" required>
        <InputLabel id="reject-code-label">Reason</InputLabel>
        <Select
          labelId="reject-code-label"
          label="Reason"
          value={code}
          onChange={(e) => setCode(e.target.value as AdmissionCode)}
        >
          {ADMISSION_CODE_VALUES.map((value) => (
            <MenuItem key={value} value={value}>
              {value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        label="Message (optional)"
        size="small"
        fullWidth
        multiline
        minRows={2}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Additional context for this rejection…"
      />
    </FormDialog>
  );
}
